import { useState, useEffect } from "react";
import API from "../lib/api";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Warning, CheckCircle, ChartBar, Users, Folder, Truck, TrendUp, ArrowRight
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

const SEVERITY_STYLES = {
  critical: { bg: "bg-red-50 dark:bg-red-950/30", border: "border-red-200 dark:border-red-800", icon: "text-red-500", badge: "bg-red-100 text-red-700" },
  warning: { bg: "bg-amber-50 dark:bg-amber-950/30", border: "border-amber-200 dark:border-amber-800", icon: "text-amber-500", badge: "bg-amber-100 text-amber-700" },
  info: { bg: "bg-blue-50 dark:bg-blue-950/30", border: "border-blue-200 dark:border-blue-800", icon: "text-blue-500", badge: "bg-blue-100 text-blue-700" },
};

const RESOURCE_ICONS = { users: Users, projects: Folder, suppliers: Truck };

function UsageBar({ label, current, limit, icon: Icon }) {
  const pct = limit > 0 ? Math.min((current / limit) * 100, 100) : 0;
  const isUnlimited = limit === -1;
  const barColor = pct >= 100 ? "bg-red-500" : pct >= 75 ? "bg-amber-500" : "bg-emerald-500";

  return (
    <div className="border border-border rounded-lg p-4 bg-card" data-testid={`usage-bar-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon size={16} className="text-muted-foreground" weight="duotone" />
          <span className="text-sm font-medium capitalize">{label}</span>
        </div>
        <span className="text-xs text-muted-foreground">
          {isUnlimited ? (
            <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">Unlimited</Badge>
          ) : (
            <span className="font-mono">{current} / {limit}</span>
          )}
        </span>
      </div>
      {!isUnlimited && (
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
        </div>
      )}
      {!isUnlimited && (
        <p className="text-[10px] text-muted-foreground mt-1">{Math.round(pct)}% used</p>
      )}
    </div>
  );
}

export default function UsageAlertsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    API.get("/usage-alerts")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load usage data"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading usage data...</div>;
  if (!data) return <div className="text-center py-20 text-muted-foreground">No usage data available</div>;

  const { alerts, usage, limits, plan } = data;
  const hasAlerts = alerts && alerts.length > 0;

  return (
    <div data-testid="usage-alerts-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
          <ChartBar size={18} className="text-white" weight="fill" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Usage & Billing Alerts</h1>
          <p className="text-xs text-muted-foreground">Monitor resource usage against your plan limits</p>
        </div>
        {plan && (
          <Badge className="ml-auto bg-primary/10 text-primary text-xs capitalize" data-testid="current-plan-badge">{plan} Plan</Badge>
        )}
      </div>

      {/* Active Alerts */}
      {hasAlerts && (
        <div className="space-y-3 mb-6" data-testid="active-alerts">
          {alerts.map((alert, i) => {
            const s = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.info;
            const Icon = alert.severity === "critical" ? Warning : alert.severity === "warning" ? Warning : ChartBar;
            return (
              <div key={i} className={`${s.bg} ${s.border} border rounded-lg p-4 flex items-start gap-3`} data-testid={`alert-${alert.resource}-${alert.severity}`}>
                <Icon size={20} className={`${s.icon} mt-0.5 shrink-0`} weight="fill" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={`${s.badge} text-[9px]`}>{alert.severity.toUpperCase()}</Badge>
                    <span className="text-sm font-semibold capitalize">{alert.resource}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{alert.message}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${alert.severity === "critical" ? "bg-red-500" : "bg-amber-500"}`} style={{ width: `${Math.min(alert.percentage, 100)}%` }} />
                    </div>
                    <span className="text-[10px] font-mono text-muted-foreground">{alert.percentage}%</span>
                  </div>
                </div>
                {alert.severity === "critical" && (
                  <button
                    onClick={() => navigate("/platform/billing")}
                    className="flex items-center gap-1 text-xs font-medium text-primary hover:underline shrink-0"
                    data-testid={`upgrade-btn-${alert.resource}`}
                  >
                    Upgrade <ArrowRight size={12} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* No Alerts */}
      {!hasAlerts && (
        <div className="border border-border rounded-lg p-8 bg-card text-center mb-6" data-testid="no-alerts">
          <CheckCircle size={32} className="mx-auto text-emerald-500 mb-2" weight="duotone" />
          <p className="text-sm font-medium">All clear!</p>
          <p className="text-xs text-muted-foreground">Your usage is well within plan limits</p>
        </div>
      )}

      {/* Usage Meters */}
      <div className="grid sm:grid-cols-3 gap-3 mb-6" data-testid="usage-meters">
        <UsageBar label="Users" current={usage?.users || 0} limit={limits?.users ?? -1} icon={Users} />
        <UsageBar label="Projects" current={usage?.projects || 0} limit={limits?.projects ?? -1} icon={Folder} />
        <UsageBar label="Suppliers" current={usage?.suppliers || 0} limit={limits?.suppliers ?? -1} icon={Truck} />
      </div>

      {/* Upgrade Prompt */}
      <div className="border border-border rounded-lg p-5 bg-gradient-to-r from-primary/5 to-transparent" data-testid="upgrade-prompt">
        <div className="flex items-center gap-3">
          <TrendUp size={24} className="text-primary" weight="duotone" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold font-[Chivo]">Need more capacity?</h3>
            <p className="text-xs text-muted-foreground">Upgrade your plan to unlock higher limits and additional features.</p>
          </div>
          <button
            onClick={() => navigate("/platform/billing")}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-medium rounded-md hover:bg-primary/90 transition-colors"
            data-testid="view-plans-btn"
          >
            View Plans <ArrowRight size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}
